import i18n from 'i18next';
import { localizarDatas, rotuloEtapaPipeline } from './rotulos';
import { censurarTexto, dataHora, dinheiro, email, mesAno, nomeDeArquivo, nomePessoa, telefoneFormatado } from './formato';
import { MODELOS_SISTEMA, ROTULOS_VALORES, type ModeloSistema, type Transformador } from './texto-sistema-modelos';

/**
 * Review r4 M5 e r5 M5: o chatter guarda só texto, e o servidor grava as mensagens de sistema em pt (com data dd/mm/aaaa).
 * Aqui reconhecemos os modelos conhecidos (`texto-sistema-modelos.ts`, um por frase que o servidor grava) e remontamos
 * a frase no idioma do app, com a data no formato do idioma. Texto que não casa com nenhum modelo passa só com as
 * datas localizadas.
 */

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const tChave = (chave: string, opcoes?: Record<string, unknown>) => i18n.t(`chatter.sistema.${chave}`, opcoes);

function rotulo(grupo: keyof typeof ROTULOS_VALORES, valor: string): string {
  const chave = (ROTULOS_VALORES[grupo] as Record<string, string>)[valor];
  return chave ? tChave(chave) : valor;
}

/** Símbolo da moeda no formato pt-BR (como o servidor grava: "R$", "US$", "€") => código ISO. Montado uma vez. */
let simbolosPt: Map<string, string> | null = null;
function moedaDoSimbolo(simbolo: string): string | null {
  if (!simbolosPt) {
    simbolosPt = new Map();
    const lista = (Intl as { supportedValuesOf?: (k: 'currency') => string[] }).supportedValuesOf?.('currency') ?? [];
    for (const c of ['BRL', 'USD', 'EUR', 'GBP', ...lista]) {
      const s = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: c }).formatToParts(1).find((p) => p.type === 'currency')?.value;
      if (s && !simbolosPt.has(s)) simbolosPt.set(s, c);
    }
  }
  return simbolosPt.get(simbolo) ?? null;
}

/**
 * Review r25 visual (O3): valor que o servidor grava no formato pt-BR ("R$ 150,00", "-R$ 1.234,56") no formato do idioma do
 * app (pelo `dinheiro` do núcleo, que também respeita o modo discreto). Fora desse formato, ou moeda desconhecida, passa como veio.
 */
export function dinheiroDoServidor(texto: string): string {
  const m = /^(-?)([^\d\s-][^\d]*?)\s*(\d{1,3}(?:\.\d{3})*),(\d{2})$/.exec(texto.trim().replace(/\u00a0/g, ' '));
  const moeda = m ? moedaDoSimbolo(m[2]!.trim()) : null;
  if (!m || !moeda) return texto;
  const centavos = Number(m[3]!.replace(/\./g, '')) * 100 + Number(m[4]);
  const novo = dinheiro(m[1] ? -centavos : centavos, moeda);
  // Em pt o texto é o mesmo (só o espaço pode ser outro): fica o que o servidor gravou.
  return novo.replace(/\s/g, ' ') === texto.replace(/\s/g, ' ') ? texto : novo;
}

function transformar(tr: Transformador | undefined, valor: string): string {
  if (!tr) return localizarDatas(valor);
  if (tr === 'data') return localizarDatas(valor);
  if (tr === 'dataHora') {
    // O servidor grava o relógio do workspace ("30/10/2026, 14:00"); a data local com os mesmos números mantém a hora no `dataHora`.
    const m = /^(\d{2})\/(\d{2})\/(\d{4}),? (\d{2}):(\d{2})$/.exec(valor.trim());
    return m ? dataHora(new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]), Number(m[4]), Number(m[5]))) : localizarDatas(valor);
  }
  if (tr === 'sufixo') return valor ? `: ${localizarDatas(valor)}` : '';
  if (tr === 'id') return UUID.test(valor) ? valor.slice(0, 8) : valor;
  if (tr === 'etapa') return rotuloEtapaPipeline(valor);
  if (tr === 'nome') return nomePessoa(valor);
  if (tr === 'mes') return mesAno(valor) || valor;
  if (tr === 'linha') return valor ? `\n${valor}` : '';
  if (tr === 'dinheiro') return dinheiroDoServidor(valor);
  if (tr === 'arquivo') return nomeDeArquivo(valor);
  // Fornecedor da cotação: rótulo conhecido ("sem fornecedor") traduzido; nome de verdade segue a opção de nomes.
  if (tr === 'rotulo:fornecedor') {
    const r = rotulo('fornecedor', valor);
    return r === valor ? nomePessoa(valor) : r;
  }
  if (tr === 'extras') {
    return valor
      .split(', ')
      .map((parte) => {
        const m = /^(juros|multa|desconto) (.+)$/.exec(parte);
        return m ? `${rotulo('extra', m[1]!)} ${dinheiroDoServidor(m[2]!)}` : parte;
      })
      .join(', ');
  }
  if (tr === 'contatosValor') {
    return valor
      .split(', ')
      .map((parte) => {
        // Anotação antiga (sem parâmetros, review r12 spec M1): o valor passa pelo formatador do tipo, como no formato novo; o de
        // e-mail mascara no modo discreto qualquer forma (IDN, sublinhado no domínio) que o texto livre não reconhece.
        const m = /^(telefone|e-mail) (.+)$/.exec(parte);
        if (!m) return parte;
        return `${rotulo('contato', m[1]!)} ${m[1] === 'e-mail' ? email(m[2]) : telefoneFormatado(m[2])}`;
      })
      .join(', ');
  }
  if (tr === 'contatos') return valor.split(' e ').map((c) => rotulo('contato', c)).join(tChave('juncao'));
  if (tr.startsWith('trecho:')) return valor ? tChave(tr.slice('trecho:'.length)) : '';
  if (tr.startsWith('rotulo:')) return rotulo(tr.slice('rotulo:'.length) as keyof typeof ROTULOS_VALORES, valor);
  return valor;
}

/** Modelo que reconhece a frase (e os valores capturados), ou null. Usado pelo teste de cobertura. */
export function modeloDoSistema(corpo: string): { modelo: ModeloSistema; capturas: string[] } | null {
  for (const modelo of MODELOS_SISTEMA) {
    const r = modelo.re.exec(corpo);
    if (r) return { modelo, capturas: modelo.params.map((_, i) => r[i + 1] ?? '') };
  }
  return null;
}

/**
 * Review r12 spec (M1): parâmetros estruturados que o servidor grava com a mensagem de sistema (`chatter_mensagens.parametros`).
 * Quando vêm, a frase é montada deles e cada valor passa pelo formatador do tipo (o de e-mail mascara qualquer forma no modo
 * discreto, IDN e sublinhado inclusive); sem eles (mensagem antiga), vale o texto, como antes.
 */
export type ParametrosSistema = { modelo: string } & Record<string, unknown>;

function contatosEstruturados(contatos: unknown): string | null {
  if (!Array.isArray(contatos) || !contatos.length) return null;
  const partes: string[] = [];
  for (const c of contatos as { tipo?: unknown; valor?: unknown }[]) {
    if (!c || typeof c.valor !== 'string') return null;
    if (c.tipo === 'email') partes.push(`${rotulo('contato', 'e-mail')} ${email(c.valor)}`);
    else if (c.tipo === 'telefone') partes.push(`${rotulo('contato', 'telefone')} ${telefoneFormatado(c.valor)}`);
    else return null;
  }
  return partes.join(', ');
}

/** Lote crm5 (E11): a entrada e a saída de verticais na linha do tempo (o servidor monta do histórico das verticais). */
function verticaisEstruturadas(p: ParametrosSistema): string | null {
  const lista = (v: unknown) => (Array.isArray(v) ? (v as { nome?: unknown; todas?: unknown; outra?: unknown }[]) : []);
  const nomes = (v: unknown) =>
    lista(v)
      .map((x) => (x.todas ? tChave('verticaisTodas') : typeof x.nome === 'string' && x.nome && !x.outra ? x.nome : tChave('verticaisOutra')))
      .join(', ');
  const partes: string[] = [];
  if (lista(p.entrou).length) partes.push(tChave('verticaisEntrou', { nomes: nomes(p.entrou) }));
  if (lista(p.saiu).length) partes.push(tChave('verticaisSaiu', { nomes: nomes(p.saiu) }));
  return partes.length ? tChave('verticaisAlteradas', { mudancas: partes.join('; ') }) : null;
}

function textoEstruturado(p: ParametrosSistema): string | null {
  if (p.modelo === 'verticais' && i18n.exists('chatter.sistema.verticaisAlteradas')) return verticaisEstruturadas(p);
  if (p.modelo !== 'contatoNaoAplicado' || !i18n.exists('chatter.sistema.contatoNaoAplicado')) return null;
  const novos = contatosEstruturados(p.contatos);
  return novos === null ? null : tChave('contatoNaoAplicado', { origem: typeof p.origem === 'string' ? p.origem : '', novos });
}

/** Rodada rlote-8 (VIS-M2): nomes que a mensagem de sistema traz nos parâmetros (o cliente da negociação), para o modo discreto. */
export function nomesDoSistema(parametros?: ParametrosSistema | null): string[] {
  return typeof parametros?.parceiro_nome === 'string' ? [parametros.parceiro_nome] : [];
}

export function textoDoSistema(corpo: string | null | undefined, parametros?: ParametrosSistema | null): string {
  if (!corpo) return '';
  const estruturado = parametros ? textoEstruturado(parametros) : null;
  if (estruturado !== null) return censurarTexto(estruturado);
  const achado = modeloDoSistema(corpo);
  if (!achado || !i18n.exists(`chatter.sistema.${achado.modelo.chave}`)) return censurarTexto(localizarDatas(corpo));
  const { modelo, capturas } = achado;
  const valores: Record<string, string> = {};
  modelo.params.forEach((p, i) => {
    valores[p] = transformar(modelo.tr?.[p], capturas[i] ?? '');
  });
  // Modo discreto: valor, documento e contato dentro da frase saem mascarados.
  return censurarTexto(tChave(modelo.chave, valores));
}

/**
 * Frase de sistema de uma mensagem da conversa. A do chat do site é montada dos dados gravados (`dados.nome`, `email`, `telefone`),
 * com o formatador de cada tipo: procurar nome e contato na frase pronta falhava com nome curto, telefone estrangeiro e nome que
 * trazia o separador ou um e-mail (rcor-8 a rcor-10).
 */
export function textoDaMensagemSistema(corpo: string | null | undefined, dados: Record<string, unknown> | null | undefined): string {
  const achado = dados?.acao === 'chat_site' ? modeloDoSistema(corpo ?? '') : null;
  if (!achado || !achado.modelo.chave.startsWith('chatSite')) return textoDoSistema(corpo);
  const lido = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : '');
  const itens = [nomePessoa(lido(dados?.nome)), lido(dados?.email) && email(lido(dados?.email)), lido(dados?.telefone) && telefoneFormatado(lido(dados?.telefone))].filter(Boolean);
  const widget = censurarTexto(achado.capturas[0] ?? '');
  return itens.length ? tChave('chatSiteComDados', { widget, dados: itens.join(' · ').replace(/\.$/, '') }) : tChave('chatSiteSemDados', { widget });
}

/**
 * Descrição ou motivo que o servidor gera em pt (ex.: "Pedido PED-1, recebimento REC-1 (parcela 1/3)", "Inventário 02/10"):
 * remonta no idioma do app quando casa com um modelo e devolve o texto exatamente como veio quando não casa
 * (texto digitado pelo usuário nunca é reescrito).
 */
export function descricaoGerada(texto: string | null | undefined): string {
  if (!texto) return '';
  return modeloDoSistema(texto) ? textoDoSistema(texto) : censurarTexto(texto);
}
